import { Injectable } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import { assignIn, isArray, isEmpty, keys, map, remove } from "lodash";
import { Observable } from "rxjs";
import { v4 as uuid } from "uuid";
import {
  Assessment,
  StaticData,
} from "../offline-asmt-data-service/offline-asmt-data-interface";
import { DatabaseProvider } from "../offline-database/db-connection";
import { DbQueries } from "../offline-database/db-queries";
import {
  ASSESSMENT_SECTION_TYPE_LOOKUP,
  ASSESSMENT_STATUS_CODE,
  ASSESSMENT_STATUS_LOOKUP,
  isNewOfflineVisit,
  NAVIGATION_SECTION_NAME,
  OFFLINE_ACTION_DATE_FORMAT,
  parseJSON,
  ROLE_LOOKUP,
  stringifyJSON,
} from "../offline-manager/offline-globals";

/*
  Generated class for the OfflineAsmtDataServiceProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class OfflineAsmtDataServiceProvider {
  public asmtTypeResp: any;
  public loggedInUserId: string;
  public loggedInUserName: string;

  constructor(public database: DatabaseProvider, private authSvc: AuthService) {
    this.asmtTypeResp = {
      assessment_roles: [],
    };
  }

  public setUserInfo() {
    const user = this.authSvc.getUser();
    if (user) {
      // this.loggedInUserName = user.profile.name;
      // const role = parseJSON(user.profile.role, [{}]);
      // this.loggedInUserId = role[0].userID;
      this.loggedInUserId = user.userId.toString();
    }
  }

  public offlineEditAssessment(offlineAction: any): Observable<any> {
    const { assessment_id, sectionId } = offlineAction;

    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.OFFLINE_ACTION_SELECT_BY_ASMT,
                [assessment_id],
                (tx, resultSet) => {
                  if (resultSet.rows.length > 0) {
                    // Update offline action
                    const dbRecord = resultSet.rows.item(0);
                    this.updateOfflineAction(offlineAction, dbRecord, tx);
                  } else {
                    // Get Patient details, Assessment type and insert offline action
                    this.prepareAndInsertOfflineAction(offlineAction, tx);
                  }
                },
                (tx, error) => {
                  console.log(
                    `SELECT ERROR (offlineEditAssessment, get patient details and type): ${error.message}`
                  );
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (offlineEditAssessment, Assessment: ${assessment_id}, Section: ${sectionId}): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(
                `Transaction ok (offlineEditAssessment, Assessment: ${assessment_id}, Section: ${sectionId})`
              );
              observer.next(offlineAction.modifiedDataDbJSON);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(
            `offlineEditAssessment.getInstance error (Assessment: ${assessment_id}, Section: ${sectionId}): ${error.message}`
          );
          observer.error(error);
          observer.complete();
        }
      );
    });
  }

  private prepareModifiedDataDbJSON(
    offlineAction: any,
    action: string = "insert"
  ) {
    let { modifiedDataDbJSON } = offlineAction;
    const updates = this.getUpdates(offlineAction, action);
    offlineAction.modifiedDataDbJSON = { ...modifiedDataDbJSON, ...updates };
  }

  private getUpdates(offlineAction: any, action: string) {
    let { updated_at } = offlineAction;

    if (!this.loggedInUserId) {
      this.setUserInfo();
    }

    let updates;
    const date = updated_at.format(OFFLINE_ACTION_DATE_FORMAT);
    if (action === "insert") {
      updates = {
        date,
        createDate: date,
        updateDate: date,
        addedBy: this.loggedInUserName,
        updatedBy: this.loggedInUserName,
        addedById: this.loggedInUserId,
        updatedById: this.loggedInUserId,
      };
    } else {
      updates = {
        updateDate: date,
        updatedBy: this.loggedInUserName,
        updatedById: this.loggedInUserId,
      };
    }
    return updates;
  }

  public prepareAndInsertOfflineAction(offlineAction, tx) {
    const { assessment_id, action, expire_at, updated_at } = offlineAction;
    let { visit_type, patient_id, patient_name } = offlineAction;

    // Get Patient details and Assessment type
    tx.executeSql(
      DbQueries.GET_ASMT_PATIENT_AND_TYPE,
      [assessment_id],
      (tx, resultSet) => {
        if (resultSet.rows.length > 0) {
          const row = resultSet.rows.item(0);
          visit_type = row.asmt_type_lookup_value;
          patient_id = row.pat_chart_id;
          offlineAction.patient_id = patient_id;
          patient_name = row.patientFullName;
        }

        const dbPayload = {
          detailList: [],
          popupList: [],
        };
        this.addChangesInDbPayload(dbPayload, offlineAction);

        // Convert JSON to string before inserting in DB
        let payloadStr = stringifyJSON(dbPayload);

        // Insert offline action in DB
        tx.executeSql(
          DbQueries.OFFLINE_ACTION_INSERT,
          [
            assessment_id,
            action,
            visit_type,
            payloadStr,
            patient_id,
            patient_name,
            expire_at,
            updated_at.valueOf(),
            null,
            null,
          ],
          (tx, rs) => {},
          (tx, error) => {
            console.log(
              `INSERT ERROR (prepareAndInsertOfflineAction): ${error.message}`
            );
          }
        );

        // Update local database
        this.updateSqliteAssessmentValues(offlineAction, tx);
      },
      (tx, error) => {
        console.log(
          `SELECT ERROR (prepareAndInsertOfflineAction): ${error.message}`
        );
      }
    );
  }

  public updateOfflineAction(offlineAction, dbRecord, tx) {
    const {
      assessment_id,
      modifiedData,
      sectionId,
      updated_at,
      expire_at,
      cardId,
      cardAction,
      cardDetailsUUID,
    } = offlineAction;

    // Update payload JSON
    let dbPayload = parseJSON(dbRecord.payload, null);

    // Update dbPayload
    if (!dbPayload || isEmpty(dbPayload)) {
      dbPayload = {
        detailList: [],
        popupList: [],
      };
    }

    // Modify existing or add new entry for Section
    let existingEntries: Array<any> = [];
    if (cardId) {
      if (cardDetailsUUID) {
        existingEntries = dbPayload.popupList.filter(
          (card) => card.assessmentDetailId === cardDetailsUUID
        );
      }
    } else {
      existingEntries = dbPayload.detailList.filter(
        (section) => section.detailType === sectionId
      );
    }

    if (existingEntries.length > 0) {
      const existingEntry = existingEntries[0];
      existingEntry.data = modifiedData;
      existingEntry.updateDate = updated_at.format(OFFLINE_ACTION_DATE_FORMAT);
      if (cardId) {
        const prevAction = existingEntry.requestMethod;
        if (prevAction === "POST" && cardAction === "DELETE") {
          // Delete newly created offline card detail entry
          remove(
            dbPayload.popupList,
            cardDetailsUUID
              ? { assessmentDetailId: cardDetailsUUID }
              : { detailType: sectionId, cardType: cardId }
          );
        } else if (cardAction === "DELETE") {
          // Delete synced card detail entry from Server
          existingEntry.requestMethod = "DELETE";
        }
      }
    } else {
      this.addChangesInDbPayload(dbPayload, offlineAction);
    }

    // Update offline action, if there are changes OR new offline visit
    if (
      dbPayload.popupList.length + dbPayload.detailList.length > 0 ||
      isNewOfflineVisit(assessment_id)
    ) {
      tx.executeSql(
        DbQueries.OFFLINE_ACTION_UPDATE,
        [
          stringifyJSON(dbPayload),
          expire_at,
          updated_at.valueOf(),
          assessment_id,
        ],
        (tx, rs) => {},
        (tx, error) => {
          console.log(`UPDATE ERROR (updateOfflineAction): ${error.message}`);
        }
      );
    } else {
      // No changes to sync, Remove offline action
      tx.executeSql(
        DbQueries.OFFLINE_ACTION_DELETE,
        [dbRecord.action_id],
        (tx, rs) => {},
        (tx, error) => {
          console.log(`DELETE ERROR (updateOfflineAction): ${error.message}`);
        }
      );
    }

    // Update local database
    offlineAction.patient_id = dbRecord.patient_id;
    this.updateSqliteAssessmentValues(offlineAction, tx);
  }

  private addChangesInDbPayload(dbPayload: any, offlineAction: any) {
    const {
      modifiedData,
      sectionId,
      updated_at,
      cardId,
      cardAction,
      modifiedDataDbJSON,
    } = offlineAction;
    const sectionChanges: any = {
      detailType: sectionId,
      data: modifiedData,
      updateDate: updated_at.format(OFFLINE_ACTION_DATE_FORMAT),
    };

    if (cardId) {
      const cardChanges: any = {
        cardType: cardId,
        requestMethod: cardAction,
        assessmentDetailId: modifiedDataDbJSON.id,
      };
      dbPayload.popupList.push({ ...sectionChanges, ...cardChanges });
    } else {
      dbPayload.detailList.push(sectionChanges);
    }
  }

  public updateSqliteAssessmentValues(offlineAction, tx) {
    const { modifiedData, cardId, sectionId, assessment_id } = offlineAction;
    if (cardId) {
      // Save listCard (single card)
      this.insertUpdateDeleteAssessmentCardValues(offlineAction, tx);
    } else {
      // Save section (multiple cards)
      for (let modifiedCardId in modifiedData) {
        this.insertOrUpdateAssessmentSectionCardValues(
          modifiedCardId,
          offlineAction,
          tx
        );
      }
      this.updateSectionStatus(assessment_id, sectionId, tx);
    }
  }

  public insertUpdateDeleteAssessmentCardValues(offlineAction, tx) {
    const { cardId, cardAction, cardDetailsUUID } = offlineAction;

    if (cardAction === "POST") {
      this.prepareModifiedDataDbJSON(offlineAction);
      const modifiedDataDbJSON = offlineAction.modifiedDataDbJSON;
      this.prepareAndInsertAssessmentDetail(
        cardId,
        modifiedDataDbJSON,
        offlineAction,
        tx
      );
    } else if (cardAction === "PUT") {
      this.prepareAndUpdateAssessmentDetail(offlineAction, tx);
    } else if (cardAction === "DELETE") {
      tx.executeSql(
        DbQueries.DELETE_ASSESSMENT_DETAILS_BY_UUID,
        [cardDetailsUUID],
        (tx, rs) => {},
        (tx, error) => {
          console.log(
            `DELETE ERROR (insertUpdateDeleteAssessmentCardValues): ${error.message}`
          );
        }
      );
    }
  }

  private prepareAndInsertAssessmentDetail(
    cardId,
    cardChanges,
    offlineAction,
    tx
  ): any {
    const { assessment_id, sectionId, patient_id, updateAtStr } = offlineAction;

    tx.executeSql(
      DbQueries.INSERT_ASSESSMENT_DETAILS_AFTER_EDIT,
      [
        cardChanges.id,
        patient_id,
        assessment_id,
        sectionId,
        cardId,
        stringifyJSON(cardChanges),
        updateAtStr,
        updateAtStr,
        this.loggedInUserId,
        this.loggedInUserId,
        true,
      ],
      (tx, rs) => {},
      (tx, error) => {
        console.log(
          `INSERT ERROR (prepareAndInsertAssessmentDetail): ${error.message}`
        );
      }
    );
  }

  private prepareAndUpdateAssessmentDetail(offlineAction, tx): any {
    const { cardDetailsUUID, updateAtStr, assessment_id } = offlineAction;
    this.prepareModifiedDataDbJSON(offlineAction, "edit");
    const modifiedDataDbJSON = offlineAction.modifiedDataDbJSON;
    // Get DB data_json
    tx.executeSql(
      DbQueries.GET_ASSESSMENT_DETAILS_BY_UUID,
      [cardDetailsUUID],
      (tx, resultSet) => {
        let sectionId;
        let cardData = {};
        if (resultSet.rows.length > 0) {
          const dbDetailsRecord = resultSet.rows.item(0);
          sectionId = dbDetailsRecord.section_type_lookup_code;
          cardData = parseJSON(dbDetailsRecord.data_json);
        }
        // Apply changes
        offlineAction.modifiedDataDbJSON = {
          ...cardData,
          ...modifiedDataDbJSON,
        };
        // Update DB data_json
        tx.executeSql(
          DbQueries.UPDATE_ASSESSMENT_DETAILS_BY_UUID,
          [
            stringifyJSON(offlineAction.modifiedDataDbJSON),
            updateAtStr,
            cardDetailsUUID,
          ],
          (tx, rs) => {
            this.updateSectionStatus(assessment_id, sectionId, tx);
          },
          (tx, error) => {
            console.log(
              `UPDATE ERROR (updateAssessmentDetail): ${error.message}`
            );
          }
        );
      }
    );
  }

  public updateSectionStatus(assessment_id, sectionId, tx) {
    tx.executeSql(
      DbQueries.GET_ASSESSMENT_STATUS_BY_SECTION,
      [sectionId, assessment_id],
      (tx, resultSet) => {
        let currentStatus;
        if (resultSet.rows.length > 0) {
          currentStatus = resultSet.rows.item(0).status_lookup_code;
        }

        if (currentStatus !== ASSESSMENT_STATUS_CODE.COMPLETED) {
          tx.executeSql(
            DbQueries.UPDATE_ASSESSMENT_STATUS,
            [ASSESSMENT_STATUS_CODE.IN_PROGRESS, sectionId, assessment_id],
            (tx, rs) => {},
            (tx, error) => {
              console.log(
                `UPDATE ERROR (updateSectionStatus): ${error.message}`
              );
            }
          );
        }
      }
    );
  }

  public insertOrUpdateAssessmentSectionCardValues(cardId, offlineAction, tx) {
    const { assessment_id, modifiedData, sectionId, updateAtStr } =
      offlineAction;
    let cardChanges = modifiedData[cardId];
    tx.executeSql(
      DbQueries.GET_ASSESSMENT_DETAILS_AFTER_EDIT,
      [assessment_id, sectionId, cardId],
      (tx, resultSet) => {
        if (resultSet.rows.length > 0) {
          // Update assessment details
          const cardData = parseJSON(resultSet.rows.item(0)["data_json"]);
          const updates = this.getUpdates(offlineAction, "edit");
          const updatedCardData = { ...cardData, ...cardChanges, ...updates };
          tx.executeSql(
            DbQueries.UPDATE_ASSESSMENT_DETAILS_AFTER_EDIT,
            [
              stringifyJSON(updatedCardData),
              updateAtStr,
              assessment_id,
              sectionId,
              cardId,
            ],
            (tx, rs) => {},
            (tx, error) => {
              console.log(
                `UPDATE ERROR (insertOrUpdateAssessmentSectionCardValues): ${error.message}`
              );
            }
          );
        } else {
          // Insert assessment details
          const updates = this.getUpdates(offlineAction, "insert");
          cardChanges = { ...cardChanges, ...updates };
          cardChanges.id = uuid();
          this.prepareAndInsertAssessmentDetail(
            cardId,
            cardChanges,
            offlineAction,
            tx
          );
        }
      },
      (tx, error) => {
        console.log(
          `SELECT ERROR (insertOrUpdateAssessmentSectionCardValues): ${error.message}`
        );
      }
    );
  }

  public deleteOfflineActions(offlineAction: any): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(DbQueries.OFFLINE_ACTION_DELETE, [
                offlineAction.actionId,
              ]);
            },
            (error) => {
              console.log(
                `Transaction ERROR (deleteOfflineActions): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (deleteOfflineActions)`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(
            `deleteOfflineActions.getInstance error: ${error.message}`
          );
          observer.complete();
        }
      );
    });
  }

  public dumpAllAssessments(
    assessments: Assessment[],
    clearPreviousRecords: boolean = true
  ): any {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            if (clearPreviousRecords) {
              tx.executeSql(DbQueries.ASSESSMENT_CLEAR);
            }

            for (let assessment of assessments) {
              this.dumpAssessment(assessment, tx);
            }
          },
          (error) => {
            console.log(
              `dumpAllAssessment transaction error: ${error.message}`
            );
          },
          () => {
            console.log(
              `dumpAllAssessment transaction completed at ${new Date()}`
            );
          }
        );
      },
      (error) => {
        console.log(`dumpAllAssessment.getInstance error: ${error.message}`);
      }
    );
  }

  public dumpAssessment(assessment: Assessment, tx: any): any {
    let {
      pat_chart_id,
      addendum,
      asmt_status_lookup_code,
      asmt_status_lookup_value,
      asmt_type_lookup_code,
      asmt_type_lookup_value,
      care_plan_status_lookup_code,
      care_plan_status_lookup_value,
      coordinationOfCareAddendum,
      duration,
      end_dte,
      performed_by,
      performed_by_id,
      role_lookup_code,
      role_lookup_value,
      started_dte,
      timezone,
      created_date,
      completed_date,
      offline_insert,
    } = assessment;

    offline_insert = offline_insert === undefined ? 0 : offline_insert;

    tx.executeSql(DbQueries.ASSESSMENT_INSERT, [
      assessment["_id"],
      assessment["_uuid"],
      pat_chart_id,
      addendum,
      asmt_status_lookup_code,
      asmt_status_lookup_value,
      asmt_type_lookup_code,
      asmt_type_lookup_value,
      care_plan_status_lookup_code,
      care_plan_status_lookup_value,
      coordinationOfCareAddendum,
      duration,
      end_dte,
      performed_by,
      performed_by_id,
      role_lookup_code,
      role_lookup_value,
      started_dte,
      timezone,
      created_date,
      completed_date,
      offline_insert,
    ]);
  }

  public getAssessments(patientId: number): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.ASSESSMENT_SELECT_BY_PATIENT,
                [patientId],
                (tx, resultSet) => {
                  var data = {
                    visits: [],
                    x_total_count: resultSet.rows.length,
                  };
                  for (var x = 0; x < resultSet.rows.length; x++) {
                    data.visits.push(
                      this.formatVisitRecord(resultSet.rows.item(x))
                    );
                  }
                  observer.next(data);
                },
                (tx, error) => {
                  console.log(
                    `SELECT error (getAssessments): ${error.message}`
                  );
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (getAssessments): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (getAssessments)`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(`getAssessments.getInstance error: ${error.message}`);
          observer.complete();
        }
      );
    });
  }

  private formatVisitRecord(visit): any {
    visit["_id"] = visit.id;
    visit["_uud"] = visit.uuid;
    delete visit.id;
    delete visit.uuid;
    visit.addendum = visit.addendum === "true";
    visit.coordinationOfCareAddendum =
      visit.coordinationOfCareAddendum === "true";
    return visit;
  }

  public dumpAllAssessmentTemplates(
    patientId,
    templates,
    clearPreviousRecords: boolean = true
  ): any {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            if (clearPreviousRecords) {
              tx.executeSql(DbQueries.ASSESSMENT_TEMPLATE_CLEAR);
            }

            for (let template of templates) {
              this.dumpAssessmentTemplate(template, tx);
            }
          },
          (error) => {
            console.log(
              `dumpAllAssessmentTemplates Patient: ${patientId}, transaction error: ${error.message}`
            );
          },
          () => {
            console.log(
              `dumpAllAssessmentTemplates transaction completed at ${new Date()}, Patient: ${patientId}`
            );
          }
        );
      },
      (error) => {
        console.log(
          `dumpAllAssessmentTemplates.getInstance Patient: ${patientId}, error: ${error.message}`
        );
      }
    );
  }

  public dumpAssessmentTemplate(template, tx: any): any {
    let {
      id,
      uuid,
      tenant_id,
      pat_chart_id,
      assessment_id,
      rec_type_lookup_code,
      tmpl_json,
      created_by,
      created_date,
      updated_by,
      updated_date,
      version,
      active_flag,
    } = template;

    tx.executeSql(DbQueries.ASSESSMENT_TEMPLATE_INSERT, [
      id,
      uuid,
      tenant_id,
      pat_chart_id,
      assessment_id,
      rec_type_lookup_code,
      tmpl_json,
      created_by,
      created_date,
      updated_by,
      updated_date,
      version,
      active_flag,
    ]);
  }

  public getAssessmentTemplate(
    assessmentId: number,
    section: string
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.ASSESSMENT_TEMPLATE_SELECT_BY_ASMT_ID_AND_SECTION,
                [assessmentId, section],
                (tx, resultSet) => {
                  var template: any = {};
                  if (resultSet.rows.length > 0) {
                    template = resultSet.rows.item(0);
                  }

                  // Get Assessment record to apply access permission
                  tx.executeSql(
                    DbQueries.ASSESSMENT_SELECT_BY_ID,
                    [assessmentId],
                    (tx, rs) => {
                      let assessment: any = {};
                      if (rs.rows.length > 0) {
                        assessment = rs.rows.item(0);
                      }

                      if (section === NAVIGATION_SECTION_NAME) {
                        // Dropdown list replacement is not needed for navigation
                        observer.next(
                          this.formatAssessmentTemplateRecord(
                            template,
                            assessment
                          )
                        );
                      } else {
                        // Get dropdown options replacement data
                        const patientId = template.pat_chart_id;
                        tx.executeSql(
                          DbQueries.TEMPLATE_REPLACEMENT_DATA_SELECT,
                          [patientId],
                          (tx, resultSet) => {
                            let replacementData: any = {};
                            if (resultSet.rows.length > 0) {
                              replacementData = resultSet.rows.item(0);
                            }

                            // Get section replacement list from navigation template
                            tx.executeSql(
                              DbQueries.ASSESSMENT_TEMPLATE_SELECT_BY_ASMT_ID_AND_SECTION,
                              [assessmentId, NAVIGATION_SECTION_NAME],
                              (tx, resultSet) => {
                                var navigation: any = {};
                                if (resultSet.rows.length > 0) {
                                  navigation = resultSet.rows.item(0);
                                }
                                replacementData.section_list =
                                  this.getSectionList(navigation);
                                observer.next(
                                  this.formatAssessmentTemplateRecord(
                                    template,
                                    assessment,
                                    replacementData
                                  )
                                );
                              }
                            );
                          }
                        );
                      }
                    }
                  );
                },
                (tx, error) => {
                  console.log(
                    `SELECT ERROR (getAssessmentTemplate) ${section}: ${error.message}`
                  );
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (getAssessmentTemplate) ${section}: ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (getAssessmentTemplate) ${section}`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(
            `getAssessmentTemplate.getInstance error: ${error.message}`
          );
          observer.complete();
        }
      );
    });
  }

  getSectionList(navigationTemplate: any) {
    let sections = [];
    if (navigationTemplate) {
      const tmplJson = parseJSON(navigationTemplate.tmpl_json, {
        type: "jsonb",
        value: "{}",
      });
      const assessmentSections = parseJSON(
        tmplJson["value"]
      ).assessmentSections;
      if (isArray(assessmentSections)) {
        sections = assessmentSections.map((section) => section.name);
      }
    }
    return sections;
  }

  public formatAssessmentTemplateRecord(
    record: any,
    assessment: any = {},
    replacementData: any = {}
  ) {
    let response: any = {
      id: record.id,
      uuid: record.uuid,
      tenantId: record.tenant_id,
      patientChartId: record.pat_chart_id,
      assessmentId: record.assessment_id,
      recTypeLookupCode: record.rec_type_lookup_code,
      createdBy: record.created_by,
      createdDte: record.created_date,
      updatedBy: record.updated_by,
      updatedDte: record.updated_date,
      version: record.version,
      activeFlg: record.active_flag === "true",
    };

    response.tmplJson = parseJSON(record.tmpl_json, {
      type: "jsonb",
      value: "{}",
    });

    if (record.rec_type_lookup_code === NAVIGATION_SECTION_NAME) {
      let tz =
        assessment && assessment.timezone
          ? assessment.timezone
          : sessionStorage.getItem("timeZone");
      response.additionalInformation = [
        {
          timezone: tz ? [tz] : [],
        },
      ];
    } else {
      // Apply permissions if Assessment record available
      if (!isEmpty(assessment)) {
        this.applyAssessmentPermissions(response, assessment);
      }

      // Replace dropdown options if replacement record available
      if (!isEmpty(replacementData)) {
        this.replaceDropdownOptions(response, replacementData);
      }
    }
    return response;
  }

  private applyAssessmentPermissions(response: any, assessment: any = {}) {
    if (!this.loggedInUserId) {
      this.setUserInfo();
    }

    const isNonEditable =
      assessment.performed_by_id !== this.loggedInUserId ||
      assessment.asmt_status_lookup_code === ASSESSMENT_STATUS_CODE.COMPLETED;
    const template = parseJSON(response.tmplJson["value"]);
    if (isArray(template.cards)) {
      template.cards.forEach((card) => {
        if (isNonEditable) {
          // Mark all cards as readonly
          card.access = "read";
          card.readonly = true;
          card.editable = false;

          // Mark all inputs as readonly
          for (let input in card.inputs) {
            card.inputs[input].readonly = true;
          }
        } else if (card.type === "listCard" || card.type === "deltaListCard") {
          // Add missing editable property to list cards
          card.editable = card.editable || true;
        }
      });
      response.tmplJson["value"] = stringifyJSON(template);
    }
  }

  private replaceDropdownOptions(response: any, replacementData: any = {}) {
    const replaceKeys = {
      $$_GET_FAMILY_CONTACTS_$$: "family_contacts",
      $$_GET_LPN_CONTACTS_$$: "lpn_contacts",
      $$_GET_HHA_CONTACTS_$$: "hha_contacts",
      $$_GET_PHYSICIAN_LIST_$$: "physician_contacts",
    };
    const template = parseJSON(response.tmplJson["value"]);
    if (!isArray(template.cards)) {
      return;
    }
    template.cards.forEach((card) => {
      const inputs = card.inputs;
      for (let input in inputs) {
        // Replace optionGroups [Family, HHA, LPN and Physicians]
        const optionGroups = inputs[input].optionGroups;
        for (let option in optionGroups) {
          const dropdownOptions = optionGroups[option];
          if (!isArray(dropdownOptions) || dropdownOptions.length !== 1) {
            continue;
          }
          const key = dropdownOptions[0];
          if (keys(replaceKeys).indexOf(key) !== -1) {
            optionGroups[option] = parseJSON(
              replacementData[replaceKeys[key]],
              []
            ).sort();
          }
        }

        // Replace options [Section list]
        let options = inputs[input].options;
        if (
          isArray(options) &&
          options.length > 0 &&
          replacementData.section_list.length > 0
        ) {
          const sectionKeyIndex = options.indexOf("$$_GET_SECTION_LIST_$$");
          if (sectionKeyIndex !== -1) {
            //Remove replacement key and add sections list
            options.splice(sectionKeyIndex, 1, ...replacementData.section_list);
          }
        }
      }
    });
    response.tmplJson["value"] = stringifyJSON(template);
  }

  public saveStaticData(staticApiData: StaticData): any {
    const { apiPath, apiResponse } = staticApiData;
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            tx.executeSql(DbQueries.STATIC_DATA_INSERT, [apiPath, apiResponse]);
          },
          (error) => {
            console.log("Static data transaction error: " + error.message);
          },
          () => {
            console.log("Static data transaction ok");
          }
        );
      },
      (error) => {
        console.log("saveStaticData.getInstance error: " + error.message);
      }
    );
  }

  public getStaticData(apiPath: string, returnType = "json"): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.STATIC_DATA_SELECT,
                [apiPath],
                (tx, rs) => {
                  var data = null;
                  if (rs.rows!.length! > 0) {
                    if (typeof rs.rows.item(0) !== "undefined") {
                      var row = rs.rows.item(0);
                      data = row.api_response;
                      if (returnType === "json") {
                        data =
                          data && typeof data === "string"
                            ? parseJSON(data)
                            : data;
                      }
                    }
                  }
                  observer.next(data);
                },
                (tx, error) => {
                  console.log(
                    "SELECT error (getStaticData): " +
                      apiPath +
                      ": " +
                      error.message
                  );
                }
              );
            },
            (error) => {
              console.log(
                "Transaction ERROR (getStaticData) " +
                  apiPath +
                  ": " +
                  error.message
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log("Transaction ok (getStaticData)" + apiPath);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log("getStaticData.getInstance error: " + error.message);
          observer.complete();
        }
      );
    });
  }

  getLevelOfCareLookupCode(patientChartId: number): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_LEVEL_OF_CARE,
            [patientChartId],
            (tx, rs) => {
              let levelOfCare = "Not Known";
              if (rs.rows.length && rs.rows.item(0).level_of_care_lookup_code) {
                levelOfCare = rs.rows.item(0).level_of_care_lookup_code;
              }
              observer.next(levelOfCare);
            },
            (_, error) => {
              console.log("SELECT LEVEL OF CARE error: " + error.message);
              observer.error(error);
            },
            () => {
              console.log("Transaction getLevelOfCareLookupCode Ok");
              observer.complete();
            }
          );
        });
      });
    });
  }

  createAsmtTypeObservable(asmtRoles, levelOfCareLookupCode) {
    let obs = [];
    for (let i = 0; i < asmtRoles.assessment_roles.length; i++) {
      let roleLookupCode = asmtRoles.assessment_roles[i].role_lookup_code;
      obs.push(
        this.prepareAssessmentTypes(roleLookupCode, levelOfCareLookupCode)
      );
    }
    return obs;
  }

  createAssessmentTypeResponse(patientChartId: number, asmtRolesAndTypes: any) {
    return new Observable((observer) => {
      this.prepareAssessmentRoles().subscribe((asmtRoles) => {
        this.getLevelOfCareLookupCode(patientChartId).subscribe(
          (levelOfCareLookupCode) => {
            const obs = this.createAsmtTypeObservable(
              asmtRoles,
              levelOfCareLookupCode
            );
            Observable.from(obs).subscribe(
              (combinedData) => {
                combinedData.subscribe((data) => {
                  asmtRolesAndTypes.assessment_roles.push(data);
                  observer.next(asmtRolesAndTypes);
                  observer.complete();
                });
              },
              (error) => {
                console.log("Assessment types resp error", error.message);
                observer.error(error);
                observer.complete();
              }
            );
          }
        );
      });
    });
  }

  prepareAssessmentRoles(): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_TEAM_ROLE,
            [],
            (tx, rs) => {
              if (rs.rows.length > 0) {
                for (var i = 0; i < rs.rows.length; i++) {
                  let data = {
                    role_lookup_code: rs.rows.item(i)["role_lookup_code"],
                    role_lookup_value:
                      ROLE_LOOKUP[`${rs.rows.item(i)["role_lookup_code"]}`],
                    assessment_types: [],
                  };
                  this.asmtTypeResp.assessment_roles.push(data);
                }
                observer.next(this.asmtTypeResp);
              }
            },
            (_, error) => {
              console.log("SELECT TEAM ROLE error: " + error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  prepareAssessmentTypes(
    roleLookupCode: string,
    levelOfCareLookupCode: string
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_ASSESSMENT_TYPE,
            [roleLookupCode, levelOfCareLookupCode],
            (tx, rs) => {
              let asmtRoles = remove(this.asmtTypeResp.assessment_roles, {
                role_lookup_code: roleLookupCode,
              });
              for (var i = 0; i < rs.rows.length; i++) {
                asmtRoles[0].assessment_types.push(rs.rows.item(i));
              }
              let data = asmtRoles[0];
              observer.next(data);
            },
            (_, error) => {
              console.log("SELECT ASSESSMENT TYPE error: " + error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  insertAssessmentDetails(asmtDetailsValue: any) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(asmtDetailsValue, (asmt) => {
              tx.executeSql(DbQueries.INSERT_ASSESSMENT_DETAILS, [
                asmt.id,
                asmt.uuid,
                asmt.tenant_id,
                asmt.assessment_id,
                asmt.pat_chart_id,
                asmt.assessment_detail_id,
                asmt.status_lookup_code,
                asmt.section_type_lookup_code,
                asmt.rec_type_lookup_code,
                asmt.data_json,
                asmt.level,
                asmt.created_by,
                asmt.created_date,
                asmt.updated_by,
                asmt.updated_date,
                asmt.version,
                asmt.active_flag,
              ]);
            });
          },
          (error) => {
            console.log(
              "insertAssessmentDetails transaction error: " + error.message
            );
          },
          () => {
            console.log("insertAssessmentDetails transaction ok");
          }
        );
      },
      (error) => {
        console.log(
          "insertAssessmentDetails.getInstance error: " + error.message
        );
      }
    );
  }

  clearAsmtDetails() {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(DbQueries.DELETE_ASSESSMENT_DETAILS);
              tx.executeSql(DbQueries.DELETE_ASSESSMENT_VALIDATION);
              observer.next();
            },
            (error) => {
              console.log(
                "clearAsmtDetails transaction error: ",
                error.message
              );
              observer.error(error);
            },
            () => {
              console.log("clearAsmtDetails transaction ok");
              observer.complete();
            }
          );
        },
        (error) => {
          console.log("clearAsmtDetails.getInstance error: ", error.message);
          observer.error(error);
        }
      );
    });
  }

  getAssessmentDetails(asmtId: number) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.SELECT_ASSESSMENT_DETAILS_SECTION_STATUS_VALUE,
                [asmtId],
                (tx, resultSet) => {
                  let response = {
                    assessmentSectionStatusValues: null,
                    statusLookupCode: null,
                    asmtTypeLookupCode: null,
                    createdBy: null,
                  };
                  if (resultSet.rows.length > 0) {
                    response.assessmentSectionStatusValues =
                      this.formatAssessmentSectionValues(resultSet);
                    response.statusLookupCode =
                      resultSet.rows.item(0).asmt_status_lookup_code;
                    response.asmtTypeLookupCode =
                      resultSet.rows.item(0).asmt_type_lookup_code;
                    response.createdBy = resultSet.rows.item(0).performed_by_id;
                  }
                  observer.next(response);
                },
                (tx, error) => {
                  console.log(
                    `SELECT ERROR (getAssessmentDetails) : ${error.message}`
                  );
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (getAssessmentDetails) : ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (getAssessmentDetails)`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(
            `getAssessmentDetails.getInstance error: ${error.message}`
          );
          observer.complete();
        }
      );
    });
  }

  formatAssessmentSectionValues(asmtSecValues) {
    try {
      const response = {};
      const secValueCount = asmtSecValues.rows.length;
      for (let i = 0; i < secValueCount; i++) {
        let code = asmtSecValues.rows.item(i).section_type_lookup_code;
        let value = asmtSecValues.rows.item(i).status_lookup_code;
        assignIn(response, {
          [code]: stringifyJSON({
            statusValue: ASSESSMENT_STATUS_LOOKUP[`${value}`],
            statusCode: value,
          }),
        });
      }
      return response;
    } catch (error) {
      console.log("Func formatAssessmentSectionValues error ", error);
    }
  }

  getAssessmentDetailsBySection(asmtId, sectionId) {
    return new Observable((observer) => {
      let response = {
        sectionStatusValues: null,
        statusLookupCode: null,
      };
      this.getAssessmentDetailsByAsmtId(asmtId, sectionId).subscribe(
        (sectionData) => {
          this.getAssessmentSection(asmtId).subscribe((resultSet) => {
            if (resultSet.rows.length > 0) {
              response.sectionStatusValues =
                this.formatAssessmentSectionValues(resultSet);
              response.statusLookupCode =
                resultSet.rows.item(0).asmt_status_lookup_code;
            }
            this.getAssessmentDataById(asmtId).subscribe((assessment) => {
              const patientId = assessment.pat_chart_id;
              const createdDate = assessment.created_date;
              this.getPreviousAsmtDetails(
                patientId,
                createdDate,
                asmtId,
                sectionId,
                "SD002.3"
              ).subscribe((previousAsmtDetail) => {
                this.getHistoryAsmtDetails(
                  patientId,
                  createdDate,
                  asmtId,
                  sectionId
                ).subscribe((historyAsmtDetail) => {
                  const sectionValues = this.formatAssessmentSectionData(
                    sectionData,
                    previousAsmtDetail,
                    historyAsmtDetail
                  );
                  assignIn(response, sectionValues);
                  observer.next(response);
                });
              });
            });
          });
        }
      );
    });
  }

  getHistoryAsmtDetails(
    patientId,
    createdDate,
    asmtId,
    sectionId
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_HISTORY_ASSESSMENT_IDS,
            [patientId, createdDate],
            (_, historyAsmtIdData) => {
              const historyAsmtIds = [];
              for (let i = 0; i < historyAsmtIdData.rows.length; i++) {
                historyAsmtIds.push(historyAsmtIdData.rows.item(i).asmt_id);
              }
              if (!isEmpty(historyAsmtIds)) {
                const sectionName =
                  ASSESSMENT_SECTION_TYPE_LOOKUP[`${sectionId}`];
                this.getCardTypeTemplate(asmtId, sectionName).subscribe(
                  (template) => {
                    this.getHistoryAsmtDetail(
                      template,
                      sectionId,
                      historyAsmtIds
                    ).subscribe((historyAsmtDetail) => {
                      observer.next(historyAsmtDetail);
                    });
                  }
                );
              } else {
                observer.next([]);
              }
            },
            (_, error) => {
              console.log(
                `GET HISTORY ASSESSMENT DETAILS error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getHistoryAsmtDetail(template, sectionId, historyAsmtIds): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const historyCardIds = [];
          if (isArray(template.cards)) {
            template.cards.forEach((card) => {
              if (
                card.cardName !== "notes" &&
                card.type === "historyListCard"
              ) {
                historyCardIds.push(card.id);
              }
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code FROM assessment_details 
                            WHERE  assessment_id IN (${historyAsmtIds.toString()}) AND section_type_lookup_code = '${sectionId}' AND
                            rec_type_lookup_code IN ('${historyCardIds.join(
                              "','"
                            )}')`;
          tx.executeSql(
            query,
            [],
            (tx, historyAssessmentDetails) => {
              let historyAsmtDetail: any = [];
              for (let x = 0; x < historyAssessmentDetails.rows.length; x++) {
                historyAsmtDetail.push(historyAssessmentDetails.rows.item(x));
              }
              observer.next(historyAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET HISTORY ASSESSMENT DETAIL error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getPreviousAsmtDetails(
    patientId,
    createdDate,
    asmtId,
    sectionId,
    roleLookupCode
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_PREVIOUS_ASSESSMENT_ID_BY_ROLE_LOOKUP,
            [patientId, createdDate, roleLookupCode],
            (_, previousAsmtIdData) => {
              let previousAsmtId: any;
              if (previousAsmtIdData.rows.length > 0) {
                previousAsmtId = previousAsmtIdData.rows.item(0).asmt_id;
              }
              if (previousAsmtId) {
                const sectionName =
                  ASSESSMENT_SECTION_TYPE_LOOKUP[`${sectionId}`];
                this.getCardTypeTemplate(asmtId, sectionName).subscribe(
                  (template) => {
                    this.getPreviousAsmtDetail(
                      template,
                      previousAsmtId
                    ).subscribe((previousAsmtDetail) => {
                      observer.next(previousAsmtDetail);
                    });
                  }
                );
              } else {
                observer.next([]);
              }
            },
            (_, error) => {
              console.log(
                `GET PREVIOUS ASSESSMENT Details error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getAssessmentDetailsByAsmtId(asmtId, sectionId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_ASSESSMENT_DETAILS_BY_ASMT_ID_AND_SECTION,
            [asmtId, sectionId],
            (_, sectionData) => {
              observer.next(sectionData);
            },
            (_, error) => {
              console.log(
                `GET ASSESSMENT DETAIL BY ASMT ID error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getAssessmentSection(asmtId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_ASSESSMENT_SECTION_STATUS_VALUE,
            [asmtId],
            (_, resultSet) => {
              observer.next(resultSet);
            },
            (_, error) => {
              console.log(
                `GET PREVIOUS ASSESSMENT SECTION error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getPreviousAsmtDetail(template, previousAssessmentId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          let cardIds = [];
          if (isArray(template.cards)) {
            template.cards.forEach((card) => {
              if (
                card.cardName !== "notes" &&
                (card.type === "listCard" ||
                  card.type === "expandableListCardWithPrevious" ||
                  card.type === "mmseCard" ||
                  card.type === "deltaListCard") &&
                card.cardName !== "addTask"
              ) {
                cardIds.push(card.id);
              }
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code FROM assessment_details 
              WHERE assessment_id = ${previousAssessmentId} AND
              rec_type_lookup_code IN ('${cardIds.join("','")}')`;
          tx.executeSql(
            query,
            [],
            (tx, previousAssessmentDetails) => {
              let previousAsmtDetail: any = [];
              for (let x = 0; x < previousAssessmentDetails.rows.length; x++) {
                previousAsmtDetail.push(previousAssessmentDetails.rows.item(x));
              }
              observer.next(previousAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET PREVIOUS ASSESSMENT DETAIL error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getAssessmentDataById(asmtId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.ASSESSMENT_SELECT_BY_ID,
            [asmtId],
            (_, assessmentData) => {
              let assessment: any = {};
              if (assessmentData.rows.length > 0) {
                assessment = assessmentData.rows.item(0);
              }
              observer.next(assessment);
            },
            (_, error) => {
              console.log(`GET ASSESSMENT DATA BY ID error: ${error.message}`);
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getCardTypeTemplate(asmtId, sectionName): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.ASSESSMENT_TEMPLATE_SELECT_BY_ASMT_ID_AND_SECTION,
            [asmtId, sectionName],
            (_, tmplJson) => {
              let templateJson: any;
              if (tmplJson.rows.length > 0) {
                templateJson = tmplJson.rows.item(0).tmpl_json;
              }
              templateJson = parseJSON(templateJson, {
                type: "jsonb",
                value: "{}",
              });
              const template = parseJSON(templateJson["value"]);
              observer.next(template);
            },
            (_, error) => {
              console.log(`SELECT TEMPLATE CARD TYPE: ${error.message}`);
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  formatAssessmentSectionData(
    sectionData,
    previousSectionData,
    historySectionData
  ) {
    try {
      const response = {};
      const secValueCount = sectionData.rows.length;
      for (let i = 0; i < secValueCount; i++) {
        let code = sectionData.rows.item(i).rec_type_lookup_code;
        let dataJson = parseJSON(sectionData.rows.item(i).data_json);
        if (response[code] && response[code]["data"]) {
          response[code]["data"].push(dataJson);
        } else {
          response[code] = { data: [dataJson] };
        }
      }
      const previousSectionCount = previousSectionData.length;
      for (let x = 0; x < previousSectionCount; x++) {
        let previousDataJson = parseJSON(previousSectionData[x].data_json);
        let previousCode = previousSectionData[x].rec_type_lookup_code;
        if (response[previousCode]) {
          assignIn(response[previousCode], { previous: previousDataJson });
        } else {
          response[previousCode] = { data: [], previous: previousDataJson };
        }
      }
      const historySectionCount = historySectionData.length;
      for (let y = 0; y < historySectionCount; y++) {
        let historyDataJson = parseJSON(historySectionData[y].data_json);
        let historyCode = historySectionData[y].rec_type_lookup_code;
        if (response[historyCode]) {
          if (response[historyCode]["history"]) {
            response[historyCode]["history"].push(historyDataJson);
          } else {
            assignIn(response[historyCode], { history: [historyDataJson] });
          }
        } else {
          response[historyCode] = { data: [], history: [historyDataJson] };
        }
      }
      return response;
    } catch (error) {
      console.log(`Func formatAssessmentSectionData error : ${error.message}`);
    }
  }

  insertAsmtValidation(validation: any) {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            map(validation, (data) => {
              const {
                patientId,
                admitVisit,
                icaVisit,
                previouseAsmt,
                openAsmt,
                patientTimezone,
              } = data;
              tx.executeSql(DbQueries.ASSESSMENT_VALIDATION_INSERT, [
                patientId,
                admitVisit,
                icaVisit,
                previouseAsmt,
                openAsmt,
                patientTimezone,
              ]);
            });
          },
          (error) => {
            console.log(
              "insertAsmtValidation transaction error: ",
              error.message
            );
          },
          () => {
            console.log("insertAsmtValidation transaction ok");
          }
        );
      },
      (error) => {
        console.log("insertAsmtValidation.getInstance error: ", error.message);
      }
    );
  }

  getLevelOfCare() {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_All_LEVEL_OF_CARE,
            [],
            (tx, rs) => {
              let levelOfCare = [];
              if (rs.rows.length > 0) {
                for (var i = 0; i < rs.rows.length; i++) {
                  levelOfCare.push(rs.rows.item(i));
                }
                observer.next(levelOfCare);
              }
            },
            (_, error) => {
              console.log("selectLevelOfCare error: ", error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
              console.log("selectLevelOfCare Transaction Ok: ");
            }
          );
        });
      });
    });
  }

  getDareStatus(qbosId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_PATIENT_DARE_STATUS,
            [qbosId],
            (tx, rs) => {
              if (rs.rows.length > 0) {
                observer.next({ dareStatus: rs.rows.item(0).dareStatus });
              }
            },
            (_, error) => {
              console.log("getDareStatus error: ", error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
              console.log("getDareStatus Transaction Ok: ");
            }
          );
        });
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/offline-asmt-data-service/offline-asmt-data-service.ts